import type { Catalog, Locale } from "../catalog.js";

/** Five ordered voices. `human` (index 2, center) is the baseline — it has no overlay.
 *  Extending this array re-divides the slider automatically (snap math uses length-1). */
export const VOICE_STOPS = ["official", "deadpan", "human", "nonna", "dark"] as const;
export type ToneId = (typeof VOICE_STOPS)[number];

/** Undefined tone ⇒ today's copy. */
export const DEFAULT_TONE: ToneId = "human";

/** A tone overlay is a sparse partial catalog: only the tonal keys an author flavored.
 *  A value may be a single string OR an array of variants that rotate over time
 *  (so e.g. the dark voice says something different every other hour). */
export type ToneOverlay = Partial<Record<string, string | readonly string[]>>;

/** Rotation window for multi-variant tonal lines — a fresh pick every other hour. */
export const TONE_ROTATE_MS = 2 * 60 * 60 * 1000;

/** Which variant of a (possibly multi-variant) tonal string shows at `nowMs`. A plain
 *  string returns itself; an array advances by one each `periodMs`, wrapping around. */
export function toneVariant(value: string | readonly string[], nowMs: number, periodMs: number = TONE_ROTATE_MS): string {
  if (typeof value === "string") return value;
  if (value.length === 0) return "";
  const bucket = Math.floor(nowMs / periodMs);
  return value[((bucket % value.length) + value.length) % value.length];
}

/** Slider value (0..100) → nearest stop index (0..length-1). */
export const stopIndex = (value: number, stops: readonly string[] = VOICE_STOPS): number =>
  Math.round((value / 100) * (stops.length - 1));

/** Stop index → the slider value that snaps the thumb onto that stop. */
export const valueForIndex = (i: number, stops: readonly string[] = VOICE_STOPS): number =>
  Math.round((i / (stops.length - 1)) * 100);

/** The ONLY catalog keys a tone overlay may contain (Scope B allowlist). Everything else —
 *  notably all safety-critical, severity, hazard-name, and structural text — is invariant.
 *  Enforced by tone-guardrail.test.ts. */
export const TONAL_KEYS: readonly string[] = [
  // alert core
  "alert.toast.new",
  "alert.toast.escalation",
  "alert.toast.inPath",
  "alert.toast.reAlert",
  "alert.toast.allClear",
  "alert.toast.tap",
  "alert.takeover.title",
  "alert.takeover.details",
  "alert.takeover.dismiss",
  // personality chrome
  "now.status.allClear",
  "now.status.stayAware",
  "now.status.sub.allClear",
  "now.activity.empty",
  "onboarding.promise.tagline",
  "onboarding.promise.continue",
  "onboarding.promise.restore",
  "onboarding.home.browse",
  "region.unsupported.title",
  "region.unsupported.body",
  "you.voice.intro",
];

/** Authored overlays, keyed `"<tone>/<locale>"`. `human` never appears (it is the base).
 *  Missing entry ⇒ neutral fallback ({}). English-first; other locales added incrementally. */
const overlays: Record<string, () => Promise<ToneOverlay>> = {
  "official/en": () => import("./official/en.js").then((m) => m.overlay),
  "official/es": () => import("./official/es.js").then((m) => m.overlay),
  "official/ca": () => import("./official/ca.js").then((m) => m.overlay),
  "deadpan/en": () => import("./deadpan/en.js").then((m) => m.overlay),
  "deadpan/es": () => import("./deadpan/es.js").then((m) => m.overlay),
  "deadpan/ca": () => import("./deadpan/ca.js").then((m) => m.overlay),
  "nonna/en": () => import("./nonna/en.js").then((m) => m.overlay),
  "nonna/es": () => import("./nonna/es.js").then((m) => m.overlay),
  "nonna/ca": () => import("./nonna/ca.js").then((m) => m.overlay),
  "dark/en": () => import("./dark/en.js").then((m) => m.overlay),
  "dark/es": () => import("./dark/es.js").then((m) => m.overlay),
  "dark/ca": () => import("./dark/ca.js").then((m) => m.overlay),
  "official/gl": () => import("./official/gl.js").then((m) => m.overlay),
  "deadpan/gl": () => import("./deadpan/gl.js").then((m) => m.overlay),
  "nonna/gl": () => import("./nonna/gl.js").then((m) => m.overlay),
  "dark/gl": () => import("./dark/gl.js").then((m) => m.overlay),
  "official/pt-PT": () => import("./official/pt-PT.js").then((m) => m.overlay),
  "deadpan/pt-PT": () => import("./deadpan/pt-PT.js").then((m) => m.overlay),
  "nonna/pt-PT": () => import("./nonna/pt-PT.js").then((m) => m.overlay),
  "dark/pt-PT": () => import("./dark/pt-PT.js").then((m) => m.overlay),
  "official/de": () => import("./official/de.js").then((m) => m.overlay),
  "deadpan/de": () => import("./deadpan/de.js").then((m) => m.overlay),
  "nonna/de": () => import("./nonna/de.js").then((m) => m.overlay),
  "dark/de": () => import("./dark/de.js").then((m) => m.overlay),
};

/** The "<tone>/<locale>" keys of every authored overlay — drives the guardrail test
 *  so any newly-registered overlay is automatically checked. */
export const REGISTERED_OVERLAYS: readonly string[] = Object.keys(overlays);

/** Resolve the (tone, locale) overlay. A missing overlay — `human`, an unwritten locale, or
 *  an unwritten tone — is normal, not an error: it yields `{}` (base-only). */
export function loadToneOverlay(tone: ToneId, locale: Locale): Promise<ToneOverlay> {
  const load = overlays[`${tone}/${locale}`];
  return load ? load() : Promise.resolve({});
}

/** Merge a base catalog with a (sparse) tone overlay, resolving any multi-variant value
 *  to the variant for `nowMs`. Overlay keys win; absent keys fall through to base. The
 *  result is a complete Catalog of strings. */
export function applyToneOverlay(base: Catalog, overlay: ToneOverlay, nowMs = 0): Catalog {
  const out: Catalog = { ...base };
  for (const k of Object.keys(overlay)) {
    const v = overlay[k];
    if (v !== undefined) out[k] = toneVariant(v, nowMs);
  }
  return out;
}
